import { VA_FORM_IDS } from '@department-of-veterans-affairs/platform-forms/constants';
import {
  CONDITIONS_IPF_TRANSFORMATION_KEY,
  TRACKING_526EZ_CONDITIONS_TRANSFORM_COUNT,
} from '../../../constants';
import {
  incrementClickCounter,
  trackAction,
} from '../../../../shared/utils/tracking/datadogRumTracking';

const CONDITIONS_WORKFLOW_TRANSFORM_ACTION =
  '21-526EZ_conditionsTransformAction';

/**
 * Get high-level summary of formData changes
 * @param {object} beforeData - FormData before migration
 * @param {object} afterData - FormData after migration
 * @returns {object} Summary of changes with pre and post states
 */
const getFormDataChangeSummary = (beforeData, afterData) => {
  return {
    pre: {
      newDisabilitiesArrayLength: Array.isArray(beforeData?.newDisabilities)
        ? beforeData.newDisabilities.length
        : 0,
      ratedDisabilitiesViewSelectedCount: Array.isArray(
        beforeData?.ratedDisabilities,
      )
        ? beforeData.ratedDisabilities.filter(d => d['view:selected'] === true)
            .length
        : 0,
      disabilityCompNewConditionsWorkflow:
        beforeData?.disabilityCompNewConditionsWorkflow,
    },
    post: {
      newDisabilitiesArrayLength: Array.isArray(afterData?.newDisabilities)
        ? afterData.newDisabilities.length
        : 0,
      ratedDisabilitiesViewSelectedCount: Array.isArray(
        afterData?.ratedDisabilities,
      )
        ? afterData.ratedDisabilities.filter(d => d['view:selected'] === true)
            .length
        : 0,
      disabilityCompNewConditionsWorkflow:
        afterData?.disabilityCompNewConditionsWorkflow,
    },
  };
};

/**
 * Track execution of flatten transformation (v1→v2) on form load
 * @param {object} beforeData - Data before transformation
 * @param {object} afterData - Data after transformation
 */
export const trackTransformToV2 = (beforeData, afterData) => {
  try {
    const formDataSummary = getFormDataChangeSummary(
      beforeData?.formData,
      afterData?.formData,
    );

    trackAction(CONDITIONS_WORKFLOW_TRANSFORM_ACTION, {
      formId: VA_FORM_IDS.FORM_21_526EZ,
      direction: 'flatten',
      pre: formDataSummary.pre,
      post: formDataSummary.post,
    });
  } catch (error) {
    // Silent fail - tracking should never break the form
  }
};

/**
 * Track execution of unflatten transformation (v2→v1) on form load
 * @param {object} beforeData - Data before transformation
 * @param {object} afterData - Data after transformation
 */
export const trackTransformToV1 = (beforeData, afterData) => {
  try {
    const formDataSummary = getFormDataChangeSummary(
      beforeData?.formData,
      afterData?.formData,
    );
    trackAction(CONDITIONS_WORKFLOW_TRANSFORM_ACTION, {
      formId: VA_FORM_IDS.FORM_21_526EZ,
      direction: 'unflatten',
      pre: formDataSummary.pre,
      post: formDataSummary.post,
    });
  } catch (error) {
    // Silent fail - tracking should never break the form
  }
};

/**
 * Returns the IPF's conditions workflow transform state as tracking
 * properties. Composed into claim lifecycle events (form start, form
 * resumption, submission success and failure) so each can be segmented
 * by data-model version and transform state.
 *
 * @param {object} [formData] - The form data
 * @returns {object} Always includes `conditionsTransformApplied` (boolean).
 *   When formData is non-empty, also includes `conditionsTransformIpfVersion` ('v1' or 'v2').
 *   When `conditionsTransformApplied` is true, also includes `conditionsTransformType`
 *   and `conditionsTransformTimestamp`.
 */
export const getConditionsTransformProperties = (formData = {}) => {
  const marker = formData?.[CONDITIONS_IPF_TRANSFORMATION_KEY];
  const hasFormData = formData != null && Object.keys(formData).length > 0;
  const version =
    formData?.disabilityCompNewConditionsWorkflow === true ? 'v2' : 'v1';

  if (!marker || typeof marker !== 'object') {
    const result = { conditionsTransformApplied: false };
    if (hasFormData) {
      result.conditionsTransformIpfVersion = version;
    }
    return result;
  }

  const properties = {
    conditionsTransformApplied: true,
    conditionsTransformIpfVersion: version,
  };
  if (typeof marker.type === 'string') {
    properties.conditionsTransformType = marker.type;
  }
  if (typeof marker.timestamp === 'string') {
    properties.conditionsTransformTimestamp = marker.timestamp;
  }
  return properties;
};

/**
 * Increments the session-scoped count of conditions workflow transformations.
 * Stored in sessionStorage so Form526EZApp can expose it as the
 * 526conditionsTransformCount Datadog RUM global context property,
 * enabling monitors on repeated transformations (loop detection).
 *
 * @returns {number} The new transform count
 */
export const incrementConditionsTransformCount = () =>
  incrementClickCounter(TRACKING_526EZ_CONDITIONS_TRANSFORM_COUNT);

/**
 * Reads the session-scoped conditions workflow transform count.
 *
 * @returns {number} The current count (0 when unset, invalid, or storage is blocked)
 */
export const getConditionsTransformCount = () => {
  try {
    const count = parseInt(
      sessionStorage.getItem(TRACKING_526EZ_CONDITIONS_TRANSFORM_COUNT) || '0',
      10,
    );
    return Number.isNaN(count) ? 0 : count;
  } catch (error) {
    // Storage access blocked - report no transforms
    return 0;
  }
};

/**
 * Track runtime errors thrown during a conditions workflow transformation.
 *
 * @param {'flatten'|'unflatten'} transformationType
 * @param {Error} error - The caught error
 */
export const trackTransformError = (transformationType, error) => {
  try {
    window.DD_RUM?.addError(error, {
      location: CONDITIONS_WORKFLOW_TRANSFORM_ACTION,
      formId: VA_FORM_IDS.FORM_21_526EZ,
      direction: transformationType,
    });
  } catch (_trackingError) {
    // Silent fail - tracking should never break the form
  }
};
